import gql from 'graphql-tag';
import { execute } from 'apollo-boost';

export const PRODUCT_PRICE_INQUIRY = gql`
  query GetProductPriceInquiry($customerId: ID!, $productIds: [ID]!) {
    getProductPriceInquiry(customerId: $customerId, productIds: $productIds) {
      productId price nettoVpc discount1 discount2 discount3
      rabat1_IZN rabat1_PCT rabat2_IZN rabat2_PCT rabat3_IZN rabat3_PCT
      rabatUkupno_IZN rabatUkupno_PCT cenaCenovnikBezPDV cenaCenovnikSaPDV
      krajnjaCenaBezPDV krajnjaCenaSaPDV tarifnaGrupa
    }
  }
`;

const PRICE_FIELDS = [
  'price', 'nettoVpc', 'discount1', 'discount2', 'discount3',
  'rabat1_IZN', 'rabat1_PCT', 'rabat2_IZN', 'rabat2_PCT', 'rabat3_IZN', 'rabat3_PCT',
  'rabatUkupno_IZN', 'rabatUkupno_PCT', 'cenaCenovnikBezPDV', 'cenaCenovnikSaPDV',
  'krajnjaCenaBezPDV', 'krajnjaCenaSaPDV', 'tarifnaGrupa',
];
// Async prices must never restore stale quantities, packages or comments.
export const getPriceInquiryPatch = (quote) => PRICE_FIELDS.reduce((patch, key) => {
  if (quote[key] !== undefined) patch[key] = quote[key];
  return patch;
}, {});

export const priceInquiryIdentity = ({ userId, customerId, organizationId, productId }) =>
  JSON.stringify([userId, customerId, organizationId, productId].map((id) => String(id ?? '')));

const unavailable = () => new Error('PRICE_INQUIRY_UNAVAILABLE');
export const createProductPriceLoader = (client, { now = Date.now, ttl = 60000 } = {}) => {
  const cache = new Map(), pending = new Map(), queue = [], cancellations = new Set();
  let scheduled = null, running = 0, generation = 0;
  let consumers = 0;
  const prioritize = (key) => {
    const index = queue.findIndex((item) => item.key === key);
    if (index > 0) queue.unshift(queue.splice(index, 1)[0]);
  };
  const settle = (item, method, value) => {
    if (pending.get(item.key) === item) pending.delete(item.key);
    item[method](value);
  };
  const invalidate = () => {
    generation += 1;
    cache.clear();
    pending.forEach((item) => item.reject(unavailable()));
    pending.clear(); queue.length = 0;
    cancellations.forEach((cancel) => cancel());
    clearTimeout(scheduled); scheduled = null;
  };
  const startBatch = (batch) => {
      const first = batch[0];
      running += 1;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const started = now(), version = generation;
      let timer, subscription, cancel;
      const timeout = new Promise((_, reject) => {
        cancel = () => {
          reject(unavailable());
          if (subscription) subscription.unsubscribe();
          if (controller) controller.abort();
        };
        cancellations.add(cancel);
        timer = setTimeout(() => {
          reject(new Error('PRICE_INQUIRY_TIMEOUT'));
          if (subscription) subscription.unsubscribe();
          if (controller) controller.abort();
        }, first.timeoutMs);
      });
      // Cache only successful, scoped results ourselves. An empty/error result
      // must not poison Apollo's cache-first retry, including after org changes.
      // Execute the existing authenticated/error-handling link, bypassing the
      // old Apollo manager's unscoped in-flight deduplication as well as cache.
      const request = new Promise((resolve, reject) => {
        subscription = execute(client.link, {
          query: PRODUCT_PRICE_INQUIRY,
          variables: { customerId: first.customerId, productIds: batch.map((item) => item.productId) },
          context: { fetchOptions: controller ? { signal: controller.signal } : {} },
        }).subscribe({ next: resolve, error: reject, complete: () => reject(unavailable()) });
      });
      Promise.race([request, timeout]).then(({ data, errors }) => {
        if (version !== generation) throw unavailable();
        if (errors?.length) throw unavailable();
        const quotes = new Map((data?.getProductPriceInquiry || []).map((quote) => [String(quote.productId), quote]));
        batch.forEach((item) => {
          const quote = quotes.get(String(item.productId));
          if (!quote || !['price', 'nettoVpc', 'krajnjaCenaBezPDV', 'krajnjaCenaSaPDV'].some((field) =>
            quote[field] != null && Number.isFinite(Number(quote[field])))) {
            settle(item, 'reject', unavailable());
            return;
          }
          cache.delete(item.key);
          cache.set(item.key, { quote, expires: now() + ttl });
          if (cache.size > 200) cache.delete(cache.keys().next().value);
          settle(item, 'resolve', quote);
        });
        if (typeof window !== 'undefined' && window.sentryTrackMetric) {
          window.sentryTrackMetric('product_price_inquiry', now() - started);
        }
      }).catch((error) => batch.forEach((item) => settle(item, 'reject', error))).finally(() => {
        clearTimeout(timer);
        if (subscription) subscription.unsubscribe();
        cancellations.delete(cancel);
        batch.forEach((item) => { if (pending.get(item.key) === item) pending.delete(item.key); });
        running -= 1;
        pump();
      });
  };
  const pump = () => {
    scheduled = null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    while (running < 2 && queue.length) {
      const first = queue.shift();
      if (!pending.has(first.key)) continue;
      const batch = [first];
      for (let i = 0; i < queue.length && batch.length < 20;) {
        if (queue[i].scope === first.scope) batch.push(queue.splice(i, 1)[0]);
        else i += 1;
      }
      startBatch(batch);
    }
  };
  const resume = () => {
    if (scheduled === null && queue.length) scheduled = setTimeout(pump, 0);
  };
  return {
    retain() {
      if (!consumers) {
        window.addEventListener('msales-products-changed', invalidate);
        window.addEventListener('online', resume);
      }
      consumers += 1;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        consumers -= 1;
        if (!consumers) {
          window.removeEventListener('msales-products-changed', invalidate);
          window.removeEventListener('online', resume);
          invalidate();
        }
      };
    },
    prioritize(input) { prioritize(priceInquiryIdentity(input)); },
    load(input, { force = false, timeoutMs = 12000, priority = false } = {}) {
      const key = priceInquiryIdentity(input);
      const cached = cache.get(key);
      if (!force && cached?.expires > now()) return Promise.resolve(cached.quote);
      if (pending.has(key)) {
        if (priority) prioritize(key);
        return pending.get(key).promise;
      }
      const item = { ...input, key, timeoutMs, scope: JSON.stringify([input.userId, input.customerId, input.organizationId]) };
      item.promise = new Promise((resolve, reject) => { item.resolve = resolve; item.reject = reject; });
      pending.set(key, item);
      if (priority) queue.unshift(item);
      else queue.push(item);
      if (scheduled === null) scheduled = setTimeout(pump, 0);
      return item.promise;
    },
    invalidate,
  };
};

const loaders = new WeakMap();
export const getProductPriceLoader = (client) => {
  if (!loaders.has(client)) loaders.set(client, createProductPriceLoader(client));
  return loaders.get(client);
};
export const clearProductPriceCache = (client) => loaders.get(client)?.invalidate();
